import dayjs from "dayjs"

/**
 * Convert temperature values between Celsius and Fahrenheit.
 *
 * @param {number|string} val - The temperature value need to convert.
 * @param {number} target - The target temperature unit (1 for ℃, 2 for ℉).
 * @param {number} [source=1] - The current temperature unit (1 for ℃, 2 for ℉).
 * @param {boolean} [isDiff=false] - Indicates if the conversion is for temperature difference.
 * @returns {number|string} The converted temperature value or the original input if invalid.
 */
export const convertTemp = (val, target = 2, source = 1, isDiff = 0) => {
  if (val === "" || val == null || isNaN(val)) return val
  const m = target - source
  const n = target < source ? -1 : 0
  const res = val * 1.8 ** m + m * 32 * 1.8 ** n * (1 - isDiff)
  return parseFloat(res.toFixed(6).toString())
}

export const convertTempObj = (obj, target = 2, source = 1, keys = null, diffKeys = []) => {
  if (target == source || !obj || typeof obj !== "object") return obj

  const res = { ...obj }
  const keysToConvert = keys || Object.keys(res)

  keysToConvert.forEach(key => {
    if (!(key in res)) return
    const value = res[key]
    const isDiff = diffKeys.includes(key)
    if (Array.isArray(value)) {
      res[key] = convertTempList(value, target, source, isDiff)
    } else if (typeof value === "number" && !isNaN(value)) {
      res[key] = convertTemp(value, target, source, isDiff)
    }
  })
  return res
}

export const convertTempList = (list, target = 2, source = 1, isDiff = 0) => {
  if (target == source) return list
  if (Array.isArray(list) && list.length > 0) {
    return list.map(item => convertTemp(item, target, source, isDiff))
  }
  return list
}

export const convertTempObjArr = (array, target = 2, source = 1, keys = null, diffKeys = []) => {
  if (target == source || !Array.isArray(array) || array.length === 0) return array

  return array.map(obj => {
    if (!obj || typeof obj !== "object") return obj

    const res = { ...obj }
    const keysToConvert = keys || Object.keys(res).filter(key => typeof res[key] === "number" && !isNaN(res[key]))

    keysToConvert.forEach(key => {
      if (!(key in res)) return
      const value = res[key]
      const isDiff = diffKeys.includes(key)

      if (typeof value === "number" && !isNaN(value)) {
        res[key] = convertTemp(value, target, source, isDiff)
      }
    })

    return res
  })
}
export const convertRTkWc = (val, target = 2, source = 1) => parseFloat((val * 3.517 ** (target - source)).toFixed(1).toString())
export const convertCOP = (val, target = 2, source = 1) => {
  const [m, n] = [
    [1, 0],
    [-1, 1]
  ][(source - 1) ^ (target - 1)]
  return parseFloat((val ** m * 3.517 ** n).toFixed(2).toString())
}

export const replaceEmpty2NaN = obj => {
  for (let key in obj) {
    if (typeof obj[key] === "object" && obj[key] !== null) {
      replaceEmpty2NaN(obj[key])
    } else if (obj[key] === null || obj[key] === undefined || obj[key] === "") {
      obj[key] = NaN
    }
  }
}

export const replaceNullWith0 = obj => {
  for (let key in obj) {
    if (typeof obj[key] === "object" && obj[key] !== null) {
      replaceNullWith0(obj[key])
    } else if (obj[key] === null || obj[key] === undefined || obj[key] === "--") {
      obj[key] = 0
    }
  }
}

export const assemblyCascadeList = (data = []) => {
  const maxSplitLength = Math.max(...data.map(item => item.split("_").length))
  const list = data.map(item => {
    let parts = item.split("_")
    while (parts.length < maxSplitLength) {
      parts.push("-")
    }
    return parts
  })

  const result = []
  list.forEach(item => {
    let currentLevel = result
    item.forEach((part, index, array) => {
      let existing = currentLevel.find(child => child.text === part)
      if (!existing) {
        existing = index === array.length - 1 ? { id: part, text: part, level: index, item: [...array] } : { id: part, text: part, level: index, children: [] }
        currentLevel.push(existing)
      }
      currentLevel = existing.children
    })
  })
  result.sort((a, b) => a.text.localeCompare(b.text, "en", { numeric: true }))
  return sortCascadeChildren(result)
}

export const sortCascadeChildren = data => {
  data.forEach(item => {
    if (item.children && item.children.length > 0) {
      sortCascadeChildren(item.children)
      item.children.sort((a, b) => a.text.localeCompare(b.text, "en", { numeric: true }))
    }
  })
  return data
}

export const getDaysInMonths = (year = new Date().getFullYear()) => {
  const daysInMonths = []
  for (let month = 0; month < 12; month++) {
    const days = new Date(year, month + 1, 0).getDate()
    daysInMonths.push(days)
  }
  return daysInMonths
}

export const getWeekdaysInMonths = (year = new Date().getFullYear()) => {
  const daysInMonths = []
  for (let month = 0; month < 12; month++) {
    const days = new Date(year, month + 1, 0).getDate()
    daysInMonths.push(days)
  }
  return daysInMonths
}

export const getWorkdaysInMonths = (year = dayjs().year(), doubleRest = true) => {
  const res = {
    weekday: Array(12).fill(0),
    weekend: Array(12).fill(0),
    wholeWeek: Array(12).fill(0),
    1: Array(12).fill(0),
    2: Array(12).fill(0),
    3: Array(12).fill(0)
  }
  Array.from({ length: 12 }).map((_, month) => {
    const start = dayjs().year(year).month(month).startOf("month")
    const end = start.endOf("month")

    let workdays = 0
    let weekends = 0

    let current = start
    while (current.isBefore(end) || current.isSame(end, "day")) {
      const day = current.day()
      const isWeekend = doubleRest ? day === 0 || day === 6 : day === 0
      isWeekend ? weekends++ : workdays++
      current = current.add(1, "day")
    }
    res.weekday[month] = workdays
    res.weekend[month] = weekends
    res.wholeWeek[month] = workdays + weekends
    res[1][month] = workdays
    res[2][month] = weekends
    res[3][month] = workdays + weekends
  })
  return res
}

export function normalizeId(id, offset = 0) {
  return typeof id === "number" || /^\d+$/.test(id) ? Number(id) : Date.now() + offset
}

export function normalizeIds(list, idField = "id") {
  return list.map((item, index) => ({
    ...item,
    [idField]: normalizeId(item[idField], index)
  }))
}

/**
 * 按字段配置将原始数据列表转换为级联树结构
 *
 * @param {Array<Object>} datas 原始数据列表
 * @param {Array<Array<string>>} levels 每层的分组字段及保留字段配置
 * 例如：
 * [
 *   ['series', 'chiller_type'],
 *   ['capacity', 'rated_lwte', 'rated_ewtc']
 * ]
 * @param {Object} [options]
 * @param {string} [options.childKey='children'] 子节点字段名
 * @param {string} [options.valueKey] 可选的节点值字段名
 * @param {string} [options.nameKey] 可选的节点名称字段名
 * @param {(a:any, b:any, field:string, level:number) => number} [options.sort] 排序函数
 *
 * @returns {Array<Object>}
 */
export const buildCascadeTree = (datas, levels, options = {}) => {
  const { childKey = "children", valueKey, nameKey, sort } = options

  if (!Array.isArray(datas)) throw new TypeError("buildCascadeTree: datas should be an array")
  if (!Array.isArray(levels) || !levels.length) throw new TypeError("buildCascadeTree: levels should be a non-empty 2D array")
  if (levels.some(item => !item?.length)) throw new TypeError("buildCascadeTree: each item in levels should be a non-empty array")

  const configuredFields = new Set(levels.flat())

  const createLevel = (data, levelIndex) => {
    const [field, ...retainFields] = levels[levelIndex]
    const isLastLevel = levelIndex === levels.length - 1
    const groupMap = new Map()

    for (const item of data) {
      const groupValue = item?.[field]
      if (!groupMap.has(groupValue)) groupMap.set(groupValue, [])
      groupMap.get(groupValue).push(item)
    }

    let entries = Array.from(groupMap.entries())

    if (typeof sort === "function") entries.sort((a, b) => sort(a[0], b[0], field, levelIndex))

    return entries.map(([groupValue, groupedItems]) => {
      const firstItem = groupedItems[0] || {}
      const node = { [field]: groupValue }

      for (const key of retainFields) node[key] = firstItem[key]
      if (valueKey) node[valueKey] = groupValue
      if (nameKey) node[nameKey] = groupValue

      if (!isLastLevel) node[childKey] = createLevel(groupedItems, levelIndex + 1)
      else {
        for (const key of Object.keys(firstItem)) {
          if (!configuredFields.has(key)) node[key] = firstItem[key]
        }
      }
      return node
    })
  }
  return createLevel(datas, 0)
}



// WEBPACK FOOTER //
// ./src/utils/convert.js